import { axiosInstance } from 'src/api/client';
import { AssistantMessage } from 'src/api/services/assistant-service';

export type LibreChatMessage = AssistantMessage & {
  id?: string;
  createdAt?: string;
};

export type LibreChatConversation = {
  id: string;
  title: string;
  createdAt?: string;
  updatedAt?: string;
};

type SendPromptArgs = {
  conversationId?: string;
  message: string;
  history?: LibreChatMessage[];
  user?: {
    id?: string;
    name?: string;
    email?: string;
    location?: string;
  };
};

export type SendPromptResult = {
  conversationId?: string;
  messages?: LibreChatMessage[];
  text?: string;
  raw?: unknown;
};

const rawBase =
  process.env.REACT_APP_LIBRECHAT_API_URL ||
  process.env.REACT_APP_LIBRECHAT_BASE_URL ||
  process.env.REACT_APP_LIBRECHAT_API_BASE ||
  '';

const LIBRECHAT_BASE = (rawBase || '/api').replace(/\/$/, '');

const buildUrl = (path: string) => {
  if (/^https?:\/\//i.test(path)) return path;
  return `${LIBRECHAT_BASE}${path.startsWith('/') ? path : `/${path}`}`;
};

type AuthConfig = {
  withCredentials: boolean;
  headers?: Record<string, string>;
};

const getAuthConfig = (): AuthConfig => {
  const envToken = process.env.REACT_APP_LIBRECHAT_TOKEN;

  // Prefer relying on browser cookies (same-origin) so the current session is used
  if (typeof document !== 'undefined') {
    return {
      withCredentials: true,
      headers: envToken ? { Authorization: `Bearer ${envToken}` } : undefined,
    };
  }

  // Fallback: use env-provided refresh token if running outside the browser
  const envRefresh = process.env.REACT_APP_LIBRECHAT_REFRESH_TOKEN;
  const tokenProvider =
    process.env.REACT_APP_LIBRECHAT_TOKEN_PROVIDER || 'librechat';
  if (envRefresh) {
    const headers: Record<string, string> = {
      Cookie: `refreshToken=${envRefresh}; token_provider=${tokenProvider}`,
    };
    if (envToken) {
      headers.Authorization = `Bearer ${envToken}`;
    }
    return { withCredentials: true, headers };
  }

  return {
    withCredentials: true,
    headers: envToken ? { Authorization: `Bearer ${envToken}` } : undefined,
  };
};

const normalizeContent = (content: unknown): string => {
  if (content == null) return '';
  if (typeof content === 'string') return content;
  if (Array.isArray(content)) {
    return content
      .map(part => {
        if (typeof part === 'string') return part;
        if (part && typeof part === 'object') {
          if (
            'text' in part &&
            typeof (part as { text?: unknown }).text === 'string'
          ) {
            return (part as { text: string }).text;
          }
          if (
            'content' in part &&
            typeof (part as { content?: unknown }).content === 'string'
          ) {
            return (part as { content: string }).content;
          }
          if ('type' in part && (part as { type?: unknown }).type === 'text') {
            const text = (part as { text?: unknown }).text;
            if (typeof text === 'string') return text;
          }
        }
        return '';
      })
      .filter(Boolean)
      .join('\n');
  }
  if (typeof content === 'object') {
    if (
      'text' in (content as { text?: unknown }) &&
      typeof (content as { text?: unknown }).text === 'string'
    ) {
      return (content as { text: string }).text;
    }
    if (
      'content' in (content as { content?: unknown }) &&
      typeof (content as { content?: unknown }).content === 'string'
    ) {
      return (content as { content: string }).content;
    }
  }
  try {
    return JSON.stringify(content);
  } catch (_e) {
    return '';
  }
};

type MaybeConversation = {
  conversationId?: string;
  id?: string;
  title?: string;
  name?: string;
  createdAt?: string;
  updatedAt?: string;
};

type ConversationsPayload =
  | { conversations?: MaybeConversation[] }
  | { data?: MaybeConversation[] }
  | MaybeConversation[]
  | unknown;

type MaybeMessage = {
  id?: string;
  role?: string;
  content?: unknown;
  text?: unknown;
  message?: unknown;
  createdAt?: string;
};

type MessagesPayload =
  | { messages?: MaybeMessage[] }
  | { data?: MaybeMessage[] }
  | MaybeMessage[]
  | unknown;

type ChatCompletionChoice = { message?: { content?: unknown } };
const DEFAULT_ENDPOINT = process.env.REACT_APP_LIBRECHAT_ENDPOINT || 'openAI';
const DEFAULT_MODEL = process.env.REACT_APP_LIBRECHAT_MODEL || 'gpt-3.5-turbo';
const STREAM_PATHS = [
  `/agents/chat/${DEFAULT_ENDPOINT}`,
  '/chat/completions',
  '/v1/chat/completions',
  '/api/chat/completions',
];
/* eslint-disable no-unused-vars, @typescript-eslint/no-unused-vars */
type StreamHandlers = {
  onToken?: (...args: unknown[]) => void;
  onConversationId?: (...args: unknown[]) => void;
  onDone?: (...args: unknown[]) => void;
  onError?: (...args: unknown[]) => void;
};
/* eslint-enable no-unused-vars, @typescript-eslint/no-unused-vars */

const generateId = () => {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `${Date.now().toString(16)}-${Math.random().toString(16).slice(2, 10)}`;
};

const NO_PARENT = '00000000-0000-0000-0000-000000000000';

const buildAgentPayload = (args: SendPromptArgs) => {
  const history = args.history ?? [];
  const lastMsg = history[history.length - 1] as
    | (LibreChatMessage & { messageId?: string; parentMessageId?: string })
    | undefined;
  const parentMessageId =
    lastMsg?.messageId ||
    lastMsg?.parentMessageId ||
    (lastMsg as { id?: string })?.id ||
    NO_PARENT;
  const messageId = generateId();
  return {
    text: args.message,
    sender: 'User',
    clientTimestamp: new Date().toISOString(),
    isCreatedByUser: true,
    parentMessageId,
    conversationId: args.conversationId,
    messageId,
    error: false,
    endpoint: DEFAULT_ENDPOINT,
    model: DEFAULT_MODEL,
    resendFiles: true,
    isTemporary: false,
    isRegenerate: false,
    isContinued: false,
    ephemeralAgent: {
      execute_code: false,
      web_search: false,
      file_search: false,
      artifacts: false,
      mcp: [],
    },
  };
};

const normalizeMessages = (incoming: unknown): LibreChatMessage[] => {
  if (!incoming) return [];
  const list = Array.isArray(incoming)
    ? incoming
    : Array.isArray((incoming as { messages?: unknown }).messages)
      ? (incoming as { messages: unknown[] }).messages
      : [];

  return list
    .map((msg, idx) => {
      if (!msg || typeof msg !== 'object') return null;
      const sender = (msg as { sender?: string }).sender;
      const explicitRole = (msg as { role?: string }).role;
      const isUserFlag = (msg as { isCreatedByUser?: boolean }).isCreatedByUser;
      let role: AssistantMessage['role'] = 'assistant';
      if (
        explicitRole === 'user' ||
        sender?.toLowerCase() === 'user' ||
        isUserFlag
      ) {
        role = 'user';
      } else if (explicitRole === 'assistant') {
        role = 'assistant';
      }
      const content =
        normalizeContent(
          (msg as { content?: unknown }).content ??
            (msg as { text?: unknown }).text ??
            (msg as { message?: unknown }).message,
        ) || '';
      return {
        id: (msg as { id?: string }).id || `${role}-${idx}`,
        role: role as AssistantMessage['role'],
        content,
        createdAt: (msg as { createdAt?: string }).createdAt,
      };
    })
    .filter(Boolean) as LibreChatMessage[];
};

const normalizeConversation = (
  raw: MaybeConversation,
): LibreChatConversation | null => {
  if (!raw || typeof raw !== 'object') return null;
  const id =
    (raw as { conversationId?: string }).conversationId ||
    (raw as { id?: string }).id;
  const title =
    (raw as { title?: string }).title ||
    (raw as { name?: string }).name ||
    'New Chat';

  if (!id) return null;

  return {
    id,
    title,
    createdAt: (raw as { createdAt?: string }).createdAt,
    updatedAt: (raw as { updatedAt?: string }).updatedAt,
  };
};

export const getLibreChatBaseUrl = () => LIBRECHAT_BASE;

export async function fetchLibreChatConversations(): Promise<
  LibreChatConversation[]
> {
  const url = buildUrl('/convos');
  const res = await axiosInstance.get<ConversationsPayload>(url, {
    params: { order: 'desc', limit: 50 },
    ...getAuthConfig(),
  });
  const data = res.data;

  const toConvoArray = (payload: ConversationsPayload): MaybeConversation[] => {
    if (
      payload &&
      typeof payload === 'object' &&
      Array.isArray(
        (payload as { conversations?: MaybeConversation[] }).conversations,
      )
    ) {
      return (payload as { conversations: MaybeConversation[] }).conversations;
    }
    if (
      payload &&
      typeof payload === 'object' &&
      Array.isArray((payload as { data?: MaybeConversation[] }).data)
    ) {
      return (payload as { data: MaybeConversation[] }).data;
    }
    if (Array.isArray(payload)) return payload as MaybeConversation[];
    return [];
  };

  const list = toConvoArray(data);
  return list
    .map(item => normalizeConversation(item as MaybeConversation))
    .filter(Boolean) as LibreChatConversation[];
}

export async function fetchLibreChatMessages(
  conversationId: string,
  signal?: AbortSignal,
): Promise<LibreChatMessage[]> {
  const url = buildUrl('/messages');
  const res = await axiosInstance.get<MessagesPayload>(url, {
    params: { conversationId, pageSize: 50, sortDirection: 'asc' },
    ...getAuthConfig(),
    signal,
  });
  const payload = res.data;

  const toMessageArray = (incoming: MessagesPayload): MaybeMessage[] => {
    if (
      incoming &&
      typeof incoming === 'object' &&
      Array.isArray((incoming as { messages?: MaybeMessage[] }).messages)
    ) {
      return (incoming as { messages: MaybeMessage[] }).messages;
    }
    if (
      incoming &&
      typeof incoming === 'object' &&
      Array.isArray((incoming as { data?: MaybeMessage[] }).data)
    ) {
      return (incoming as { data: MaybeMessage[] }).data;
    }
    if (Array.isArray(incoming)) return incoming as MaybeMessage[];
    return [];
  };

  const rows = toMessageArray(payload);
  return normalizeMessages(rows);
}

export async function deleteLibreChatConversation(
  conversationId: string,
): Promise<void> {
  const url = buildUrl('/convos');
  await axiosInstance.delete(url, {
    data: { conversationId, source: 'button' },
    ...getAuthConfig(),
  });
}

export async function sendLibreChatPrompt(
  args: SendPromptArgs,
): Promise<SendPromptResult> {
  const payload = {
    conversationId: args.conversationId,
    messages: [
      ...(args.history ?? []).map(msg => ({
        role: msg.role,
        content: msg.content,
      })),
      { role: 'user', content: args.message },
    ],
    stream: false,
    metadata: args.user,
    endpoint: DEFAULT_ENDPOINT,
    model: DEFAULT_MODEL,
  };

  const candidatePaths = [
    '/chat/completions',
    '/v1/chat/completions',
    '/api/chat/completions',
  ];
  let lastError: unknown;

  for (const path of candidatePaths) {
    try {
      const authConfig = getAuthConfig();
      const res = await axiosInstance.post(buildUrl(path), payload, {
        headers: {
          'Content-Type': 'application/json',
          ...authConfig.headers,
        },
        withCredentials: authConfig.withCredentials,
      });
      const data = res.data ?? {};

      const messages = normalizeMessages(
        (data as { messages?: unknown[] }).messages ??
          (data as { choices?: ChatCompletionChoice[] }).choices?.map(
            (choice: ChatCompletionChoice) => ({
              role: 'assistant' as const,
              content: choice.message?.content,
            }),
          ),
      );

      const textCandidate =
        (data as { message?: string }).message ||
        (data as { content?: string }).content ||
        (data as { text?: string }).text ||
        (data as { choices?: { message?: { content?: string } }[] })
          .choices?.[0]?.message?.content ||
        '';

      const text =
        textCandidate ||
        (messages.length > 0
          ? messages[messages.length - 1].content
          : undefined);

      const conversationId =
        (data as { conversationId?: string }).conversationId ||
        (data as { conversation_id?: string }).conversation_id ||
        args.conversationId;

      if (!messages.length && text) {
        messages.push({ role: 'user', content: args.message });
        messages.push({ role: 'assistant', content: text });
      }

      return { conversationId, messages, text, raw: data };
    } catch (err) {
      lastError = err;
    }
  }

  throw lastError ?? new Error('Unable to reach LibreChat backend');
}

const extractChunkText = (raw: string): string => {
  try {
    const data = JSON.parse(raw);
    const maybeChoice = (data as { choices?: ChatCompletionChoice[] })
      .choices?.[0];
    const deltaContent = (maybeChoice as { delta?: { content?: unknown } })
      ?.delta?.content;
    const messageContent = (maybeChoice as { message?: { content?: unknown } })
      ?.message?.content;

    const fromChoice =
      normalizeContent(deltaContent) || normalizeContent(messageContent);

    const otherText = normalizeContent(
      (data as { message?: unknown }).message ||
        (data as { message?: { content?: unknown } }).message?.content ||
        (data as { response?: unknown }).response ||
        (data as { content?: unknown }).content ||
        (data as { text?: unknown }).text,
    );
    return fromChoice || otherText;
  } catch (_e) {
    return '';
  }
};

export async function streamLibreChatPrompt(
  args: SendPromptArgs,
  handlers: StreamHandlers,
): Promise<void> {
  const authConfig = getAuthConfig();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(authConfig.headers ?? {}),
  };
  const agentPayload = buildAgentPayload(args);
  const completionPayload = {
    conversationId: args.conversationId,
    messages: [
      ...(args.history ?? []).map(msg => ({
        role: msg.role,
        content: msg.content,
      })),
      { role: 'user', content: args.message },
    ],
    stream: true,
    metadata: args.user,
    endpoint: DEFAULT_ENDPOINT,
    model: DEFAULT_MODEL,
  };

  let res: Response | null = null;
  for (const path of STREAM_PATHS) {
    try {
      const candidate = await fetch(buildUrl(path), {
        method: 'POST',
        headers,
        credentials: authConfig.withCredentials ? 'include' : 'same-origin',
        body: JSON.stringify(
          path.includes('/agents/chat') ? agentPayload : completionPayload,
        ),
      });
      if (candidate.ok && candidate.body) {
        res = candidate;
        break;
      }
    } catch (_e) {
      // try next path
    }
  }

  if (!res || !res.ok) {
    handlers.onError?.(
      new Error(
        `Stream request failed${
          res ? ` with status ${res.status}` : ', no available endpoint'
        }`,
      ),
    );
    return;
  }

  if (!res.body) {
    handlers.onError?.(new Error('No stream body'));
    return;
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let assistantText = '';

  try {
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const { done, value } = await reader.read();
      if (done) {
        handlers.onDone?.(assistantText);
        break;
      }
      const chunk = decoder.decode(value, { stream: true });
      const lines = chunk
        .split('\n')
        .map(l => l.trim())
        .filter(Boolean);
      for (const line of lines) {
        if (!line.startsWith('data:')) continue;
        const dataStr = line.replace(/^data:\s*/, '');
        if (dataStr === '[DONE]') {
          handlers.onDone?.(assistantText);
          return;
        }
        try {
          const parsed = JSON.parse(dataStr);
          // Skip pure user or empty events
          if ((parsed as { sender?: string }).sender === 'User') {
            continue;
          }
          const text = extractChunkText(dataStr);
          if (text) {
            assistantText += text;
            handlers.onToken?.(text);
          }
          const cid =
            (parsed as { conversationId?: string }).conversationId ||
            (parsed as { conversation_id?: string }).conversation_id;
          if (cid) {
            handlers.onConversationId?.(cid);
          }
        } catch (_e) {
          // ignore parse errors for metadata
        }
      }
    }
  } catch (err) {
    handlers.onError?.(err);
  } finally {
    reader.releaseLock();
  }
}
